
import { forwardRef, useImperativeHandle, useState } from "react"

import { TabsProps, Flex, Space, Tooltip, Button, Drawer, Tabs, Form, Segmented, Divider } from "antd"
import { Dayjs } from "dayjs"

import variables from "@/_styles/variables.module.scss"
import { SvgImage } from "@/components/_Shared/SvgImage/SvgImage"
import AnalyticsFiltersSettings from "@/components/Analytics/AnalyticsSettings/AnalyticsFiltersSettings"
import classes from "@/components/Analytics/AnalyticsSettings/AnalyticsFiltersSettings.module.scss"
import { useAnalyticsHelpers } from "@/components/Analytics/useAnalyticsHelpers"

import { useCustomerClientsSelectListQuery } from "@/hook/CustomerClients/useCustomerClientQuery"
import { useCustomerCategorySelectListQuery } from "@/hook/Dictionaries/Customers/useCustomerCategoryQuery"
import { useDepotSelectListQuery } from "@/hook/Dictionaries/useDepotQuery"
import { useFuelTypeQuery } from "@/hook/Dictionaries/useFuelTypeQuery"

import {
  AnalyticsDefaultOptions, AnalyticsDeliveryTypes,
  AnalyticsOptionValue, AnalyticsPeriod,
  FilterType,
  IAnalyticsSettingsOptions
} from "@/types/IAnalytics"
import { DeliveryType } from "@/types/IOrder"

export interface AnalyticsHeaderRefProps {
    submitFilters(): void
    close(): void
    cancelFilters(): void
}

interface AnalyticsHeaderProps {
  optionsList: IAnalyticsSettingsOptions[]
  filterOptions?: FilterType[] | undefined
  draftOptionsList: IAnalyticsSettingsOptions[]
  setOptionsList: (options: IAnalyticsSettingsOptions[]) => void
  setDraftOptionsList: (options: IAnalyticsSettingsOptions[]) => void
  setCompare: (compare: boolean) => void
  setDeliveryType: (type: DeliveryType) => void
  setDraftCompare: (compare: boolean) => void
  setDraftDeliveryType: (type: DeliveryType) => void
  onCompareChange: (checked: boolean) => void
  onDeliveryTypeChange?: ((deliveryType: DeliveryType) => void) | undefined
  getOnPeriodChangeFunction: (optionsId: number) => ((period: AnalyticsPeriod) => void) | undefined
  getOnRangeChangeFunction: (optionsId: number) => ((dates: [start: Dayjs | null, end: Dayjs | null] | null, dateStrings: [string, string]) => void) | undefined
  getOnFuelTypeChangeFunction: (optionsId: number) => ((fuelTypeId: (number | undefined), option: AnalyticsOptionValue | AnalyticsOptionValue[]) => void) | undefined
  getOnDepotChangeFunction: (optionsId: number) => ((depotId: (number | undefined), option: AnalyticsOptionValue | AnalyticsOptionValue[]) => void) | undefined
  getOnCustomerClientChangeFunction: (optionsId: number) => ((customerClientId: (number | undefined), option: AnalyticsOptionValue | AnalyticsOptionValue[]) => void) | undefined
  // eslint-disable-next-line max-len
  getOnCustomerCategoryChangeFunction: (optionsId: number) => ((customerCategoryId: (number | undefined), option: AnalyticsOptionValue | AnalyticsOptionValue[]) => void) | undefined
  isComparison: boolean
  draftIsComparison: boolean,
  deliveryType?: DeliveryType
}

export const AnalyticsFiltersHeader = forwardRef<AnalyticsHeaderRefProps, AnalyticsHeaderProps>((props, ref) => {
  const {
    optionsList,
    filterOptions,
    draftOptionsList,
    setOptionsList,
    setDraftOptionsList,
    setCompare,
    setDeliveryType,
    setDraftCompare,
    setDraftDeliveryType,
    onCompareChange,
    getOnPeriodChangeFunction,
    getOnRangeChangeFunction,
    getOnFuelTypeChangeFunction,
    getOnDepotChangeFunction,
    getOnCustomerClientChangeFunction,
    getOnCustomerCategoryChangeFunction,
    onDeliveryTypeChange,
    isComparison,
    draftIsComparison,
    deliveryType
  } = props

  const { isFilterTypeSetOn, deepCopyOptionsObject } = useAnalyticsHelpers({})
  
  const [activeTabKey, setActiveTabKey] = useState("Base")
  const [openFilters, setOpenFilters] = useState<boolean>(false)
  const { data: clients } = useCustomerClientsSelectListQuery(isFilterTypeSetOn(filterOptions, FilterType.Customer))
  const { data: clientCategories } = useCustomerCategorySelectListQuery(isFilterTypeSetOn(filterOptions, FilterType.CustomerCategory))
  const { data: depots } = useDepotSelectListQuery(isFilterTypeSetOn(filterOptions, FilterType.Depot))
  const { data: fuelTypes } = useFuelTypeQuery(undefined, isFilterTypeSetOn(filterOptions, FilterType.Fuel))


  useImperativeHandle(ref, () => ({
        submitFilters() {
            submitHandler()
        },
        close() {
            onCloseFiltersHandler()
        },
        cancelFilters() {
          cancelHandler()
        }
    }))

  const onOpenFiltersHandler = () => {
    // Синхронизировать draft состояние с текущим состоянием при открытии
    setDraftOptionsList(deepCopyOptionsObject(optionsList))
    setDraftCompare(isComparison)
    setDraftDeliveryType(deliveryType || DeliveryType.All)
    setOpenFilters(true)
    setActiveTabKey(isComparison ? "Comparison" : "Base")
  }

  const onCloseFiltersHandler = () => {
    setOpenFilters(false)
  }

  const submitHandler = () => {
    // Применить фильтры: скопировать из draft в основное состояние
    setOptionsList(deepCopyOptionsObject(draftOptionsList))
    setCompare(draftIsComparison)
    setDeliveryType(deliveryType || DeliveryType.All)
  }

  const tabsProps: TabsProps["items"] = [
    {
      key: "Base",
      label: "Фильтр"
    },
    {
      key: "Comparison",
      label: "Сравнить"
    }
  ]

  const tabsHandler = (key: string) => {
    setActiveTabKey(key)
    if (key === "Comparison") {
      onCompareChange(true)
    } else {
      onCompareChange(false)
    }
  }

  const resetFields = (value: number) => {
    getOnRangeChangeFunction(value)?.call(undefined, null, ["", ""])
    getOnFuelTypeChangeFunction(value)?.call(undefined, undefined, [])
    getOnDepotChangeFunction(value)?.call(undefined, undefined, [])
    getOnCustomerClientChangeFunction(value)?.call(undefined, undefined, [])
    getOnCustomerCategoryChangeFunction(value)?.call(undefined, undefined, [])
  }

  const cancelHandler = () => {

    resetFields(1)

    if (draftIsComparison || isComparison) {
      resetFields(2)
    }
    // Сбросить все фильтры на значения по умолчанию

    const clearedOptions = [AnalyticsDefaultOptions]

    // Обновить draft состояние
    setDraftOptionsList(deepCopyOptionsObject(clearedOptions))
    setDraftCompare(false)
    setDraftDeliveryType(DeliveryType.All)

    // Применить очищенные фильтры сразу
    setOptionsList(deepCopyOptionsObject(clearedOptions))
    setCompare(false)
    setDeliveryType(DeliveryType.All)

    setActiveTabKey("Base")

    if (isFilterTypeSetOn(filterOptions, FilterType.DeliveryType) && onDeliveryTypeChange) {
      onDeliveryTypeChange(DeliveryType.All)
    }

  }

  return (
    <Flex gap="small" vertical>
      <Space>
        <Tooltip title="Фильтр">
          <Button
            type="default" variant="outlined"
            style={{ borderColor: variables.basecolorlight }}
            icon={<SvgImage src="images/filter-icon.svg" width={16} height={12} />}
            onClick={onOpenFiltersHandler}
          />
        </Tooltip>
      </Space>
      <Drawer
        open={openFilters}
        title="Фильтр"
        width={draftIsComparison ? 860 : 378}
        styles={{ wrapper: { transition: "2s" } }}
        onClose={onCloseFiltersHandler}
        footer={
          <Flex vertical gap="small">
            <Button
              block
              size="large"
              type="primary"
              onClick={submitHandler}
            >
              Применить
            </Button>
            <Button
              block
              size="large"
              color="primary"
              variant="outlined"
              onClick={cancelHandler}
            >
              Сбросить
            </Button>
          </Flex>
        }
      >
        <Tabs items={tabsProps} activeKey={activeTabKey} onChange={tabsHandler} className="customTab" destroyInactiveTabPane={false} />
        {
          isFilterTypeSetOn(filterOptions, FilterType.DeliveryType) &&
          <Form.Item>
            <Segmented
              options={AnalyticsDeliveryTypes}
              value={deliveryType}
              onChange={onDeliveryTypeChange}
            />
          </Form.Item>
        }

        <Flex gap={60}>

          {draftOptionsList.map((options, index) => {
            const comparisonIndex = index + 1
            return <Flex gap={60} key={"analytics-settings"+index}>
              {
                draftIsComparison && index > 0 &&
                <Divider className={classes["comparison-options-divider"]} type="vertical" />
              }

              <AnalyticsFiltersSettings
                fuelTypes={fuelTypes}
                depots={depots?.response}
                customerClients={clients?.response}
                customerCategories={clientCategories?.response}
                options={options}
                filterOptions={filterOptions}
                onRangeChange={getOnRangeChangeFunction(comparisonIndex)}
                onPeriodChange={getOnPeriodChangeFunction(comparisonIndex)}
                onChosenFuelTypeChange={getOnFuelTypeChangeFunction(comparisonIndex)}
                onChosenDepotChange={getOnDepotChangeFunction(comparisonIndex)}
                onCustomerClientChange={getOnCustomerClientChangeFunction(comparisonIndex)}
                onCustomerCategoryChange={getOnCustomerCategoryChangeFunction(comparisonIndex)} />
            </Flex>
          })}
        </Flex>
      </Drawer>
    </Flex>
  )
})